import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Section } from '@/components/atoms/Section';
import { Button } from '@/components/atoms/Button';
import { Input as BaseInput } from '@/components/atoms/Form/Input';
import { Text } from '@/components/atoms/Text';
import { computeText } from '@/locales/utils';
import { EwalletInputWrapper, SectionActions, ChangesInfo, ErrorText } from '../RestaurantInfo.style';

type EwalletMinimumSectionProps = {
    value: number;
    onChange: (value: number) => void;
    currency: string;
    changesCount: number;
    isSaving: boolean;
    onSave: () => Promise<void>;
    onCancel: () => void;
};

export const EwalletMinimumSection: React.FunctionComponent<EwalletMinimumSectionProps> = ({
    value,
    onChange,
    changesCount,
    isSaving,
    onSave,
    onCancel,
}) => {
    const intl = useIntl();
    const [saveError, setSaveError] = useState<string>();

    const handleSave = async () => {
        try {
            setSaveError(undefined);
            await onSave();
        } catch {
            setSaveError(computeText(intl, 'restaurantInfo.save.error'));
        }
    };

    const displayValue = value / 100;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const num = parseFloat(e.target.value);
        onChange(Number.isNaN(num) ? 0 : Math.round(num * 100));
    };

    return (
        <Section title="restaurantInfo.ewalletMinimum.title">
            <EwalletInputWrapper>
                <BaseInput
                    labelId="restaurantInfo.ewalletMinimum.inputTitle"
                    type="number"
                    value={displayValue}
                    onChange={handleChange}
                    min={0}
                    step={0.01}
                    unitType="currency"
                />
                <Text text="restaurantInfo.ewalletMinimum.caseZero" type="small" />
            </EwalletInputWrapper>
            {changesCount > 0 && (
                <SectionActions>
                    <ChangesInfo>
                        <FormattedMessage id="savingBar.changesDone" values={{ changesCount }} />
                    </ChangesInfo>
                    <Button buttonType="secondary" onClick={onCancel} isLoading={isSaving}>
                        <FormattedMessage id="button.cancel" />
                    </Button>
                    <Button buttonType="primary" onClick={handleSave} isLoading={isSaving}>
                        <FormattedMessage id="button.save" />
                    </Button>
                </SectionActions>
            )}
            {saveError && <ErrorText>{saveError}</ErrorText>}
        </Section>
    );
};
